import { useHeaderLayout } from '@/store/useHeaderLayout';
import { useCallback, useEffect, useLayoutEffect } from 'react';
import { useInView } from 'react-intersection-observer';

// Home page only: uses directional visibility so the header CTA appears only after scrolling past the products section
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

type UseShowHomeHeaderCtaOnScrollOptions = {
  initiallyShown?: boolean;
  enabled?: boolean;
};

export const useShowHomeHeaderCtaOnScroll = ({
  initiallyShown,
  enabled = true,
}: UseShowHomeHeaderCtaOnScrollOptions = {}) => {
  const { ref, inView, entry } = useInView({ initialInView: false });
  const setState = useHeaderLayout(s => s.setState);

  const showAdditionalHeaderCTA = useCallback(
    (val: boolean) => {
      setState({
        showAdditionalHeaderCTA: val,
      });
    },
    [setState],
  );

  useIsomorphicLayoutEffect(() => {
    if (!enabled) {
      setState({ showAdditionalHeaderCTA: false });
      return;
    }

    const isRefAboveViewport = (entry?.boundingClientRect.top ?? 0) < 0;

    setState({
      showAdditionalHeaderCTA: Boolean(initiallyShown) || (!inView && isRefAboveViewport),
    });

    return () => {
      setState({
        showAdditionalHeaderCTA: false,
      });
    };
  }, [enabled, entry, inView, setState, initiallyShown]);

  return {
    ref,
    showAdditionalHeaderCTA,
  };
};
